import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import useAuthStore from '../store/authStore';
import { Eye, EyeOff, AlertCircle, ArrowLeft, ArrowRight, User, Lock, Check } from 'lucide-react';
import LoginScene from '../components/common/LoginScene';

const BRAND = { fa: 'تکنوپالایش', en: 'Technopalayesh', ru: 'Технопалайеш' };
const STAGE_COLORS = ['#fbbf24', '#818cf8', '#38bdf8', '#38bdf8', '#34d399'];
const COPY = {
  fa: { system: 'سامانه‌ی مدیریت کارخانه‌ی ایزوگام', heading: 'ورود به سامانه', hint: 'با نام کاربری و رمزی که مدیر سیستم به شما داده وارد شوید.',
        user: 'نام کاربری', pass: 'رمز عبور', submit: 'ورود', bad: 'نام کاربری یا رمز عبور اشتباه است.', caps: 'Caps Lock روشن است',
        stages: ['ذوب قیر', 'اختلاط', 'پوشش‌دهی', 'رول‌پیچی', 'بارگیری'] },
  en: { system: 'Isogam factory management', heading: 'Sign in', hint: 'Use the username and password your administrator gave you.',
        user: 'Username', pass: 'Password', submit: 'Sign in', bad: 'Wrong username or password.', caps: 'Caps Lock is on',
        stages: ['Melting', 'Mixing', 'Coating', 'Winding', 'Loading'] },
  ru: { system: 'Управление заводом гидроизоляции', heading: 'Вход', hint: 'Используйте логин и пароль, выданные администратором.',
        user: 'Имя пользователя', pass: 'Пароль', submit: 'Войти', bad: 'Неверное имя пользователя или пароль.', caps: 'Включён Caps Lock',
        stages: ['Плавка', 'Смешивание', 'Покрытие', 'Намотка', 'Погрузка'] },
};

export default function Login() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [form, setForm] = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [state, setState] = useState('idle');  // idle | loading | done
  const [error, setError] = useState('');
  const [caps, setCaps] = useState(false);
  const [errKey, setErrKey] = useState(0);
  const brand = useRef(null);

  const lang = ['fa', 'en', 'ru'].includes(i18n.language) ? i18n.language : 'fa';
  const c = COPY[lang];
  const rtl = lang === 'fa';

  useEffect(() => { document.documentElement.dir = rtl ? 'rtl' : 'ltr'; document.documentElement.lang = lang; }, [rtl, lang]);
  useEffect(() => {
    const saved = localStorage.getItem('saved_username');
    if (saved) setForm(f => ({ ...f, username: saved }));
  }, []);

  // pointer parallax: the scene layers lean toward the cursor (desktop only, skipped for reduced motion)
  useEffect(() => {
    const el = brand.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.matchMedia('(pointer: coarse)').matches) return;
    let raf = 0;
    const move = (e) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', (((e.clientX - r.left) / r.width) - 0.5).toFixed(3));
        el.style.setProperty('--my', (((e.clientY - r.top) / r.height) - 0.5).toFixed(3));
      });
    };
    window.addEventListener('pointermove', move);
    return () => { window.removeEventListener('pointermove', move); cancelAnimationFrame(raf); };
  }, []);

  const changeLanguage = (l) => { i18n.changeLanguage(l); localStorage.setItem('language', l); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (state !== 'idle') return;
    setState('loading');
    setError('');
    try {
      const r = await authAPI.login({ username: form.username.trim(), password: form.password });
      const token = r.data.access_token;
      localStorage.setItem('token', token);
      const me = await authAPI.me();
      localStorage.setItem('saved_username', form.username.trim());
      setState('done');
      setTimeout(() => { setAuth(me.data, token); navigate('/'); }, 650);
    } catch {
      setError(c.bad);
      setErrKey(k => k + 1);
      setState('idle');
    }
  };

  const Arrow = rtl ? ArrowLeft : ArrowRight;

  return (
    <div className={`login-shell lgx ${state === 'done' ? 'is-done' : ''}`} dir={rtl ? 'rtl' : 'ltr'}>
      {/* ---------- brand: the production line */}
      <section className="lgx-brand" ref={brand}>
        <div className="hero-membrane" aria-hidden="true"><i /><i /><i /></div>
        <header className="lgx-head">
          <h1 className="lgx-title" key={lang}>{BRAND[lang]}</h1>
          <p className="lgx-system">{c.system}</p>
        </header>

        <div className="lgx-scene"><LoginScene /></div>

        <ol className="lgx-stages" aria-label={c.system}>
          {c.stages.map((s, i) => (
            <li key={i} style={{ '--c': STAGE_COLORS[i], '--i': i }}><span>{s}</span></li>
          ))}
        </ol>
      </section>

      {/* ---------- form */}
      <section className="lgx-panel">
        <div className="lgx-langs" role="group" aria-label="Language">
          {['fa', 'en', 'ru'].map(l => (
            <button key={l} type="button" className={l === lang ? 'on' : ''} onClick={() => changeLanguage(l)} aria-pressed={l === lang}>
              {l === 'fa' ? 'فا' : l.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="lgx-card">
          <form className="lgx-form" onSubmit={handleSubmit}>
            <h2>{c.heading}</h2>
            <p className="lgx-hint">{c.hint}</p>

            <label className="lgx-field">
              <span>{c.user}</span>
              <div className="lgx-input">
                <User size={17} aria-hidden="true" />
                <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  autoComplete="username" autoCapitalize="none" spellCheck={false} dir="ltr" required autoFocus={!form.username} />
              </div>
            </label>

            <label className="lgx-field">
              <span>{c.pass}</span>
              <div className="lgx-input">
                <Lock size={17} aria-hidden="true" />
                <input type={showPass ? 'text' : 'password'} value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  onKeyUp={e => setCaps(e.getModifierState && e.getModifierState('CapsLock'))}
                  autoComplete="current-password" dir="ltr" required autoFocus={!!form.username} />
                <button type="button" className="lgx-eye" onClick={() => setShowPass(s => !s)} aria-label={showPass ? 'hide password' : 'show password'}>
                  {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {caps && <em className="lgx-caps">{c.caps}</em>}
            </label>

            {error && <div className="lgx-error" role="alert" key={errKey}><AlertCircle size={16} /> {error}</div>}

            <button type="submit" className={`lgx-submit ${state}`} disabled={state !== 'idle'}>
              {state === 'loading' ? <span className="lgx-spin" /> : state === 'done' ? <Check size={22} strokeWidth={3} /> : <>{c.submit} <Arrow size={18} /></>}
            </button>
          </form>
        </div>

        <p className="lgx-foot">© {new Date().getFullYear()} {BRAND[lang]}</p>
      </section>
    </div>
  );
}
